/**
 * AdminNotificationsTab - Admin UI for managing push notifications
 * Allows admins to send notifications to users and view delivery stats
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, Send, Users, CheckCircle, XCircle, AlertCircle, 
  History, RefreshCw, Loader2, Target
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { safeFetch } from '../../utils/safeFetch';

import { API_URL } from '../../utils/api';

export function AdminNotificationsTab({ token }) {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [notificationType, setNotificationType] = useState('general');
  const [targetClubId, setTargetClubId] = useState('');
  const [url, setUrl] = useState('');

  // Load initial data
  useEffect(() => {
    loadData();
    loadClubs();
  }, [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, historyRes] = await Promise.all([
        safeFetch(`${API_URL}/api/push/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        safeFetch(`${API_URL}/api/push/admin/history?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (statsRes.data) setStats(statsRes.data);
      if (historyRes.data) setHistory(historyRes.data.notifications || []);
    } catch (err) {
      setError('Failed to load notification data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadClubs = async () => {
    try {
      const res = await safeFetch(`${API_URL}/api/clubs`);
      if (res.data) {
        setClubs(res.data);
      }
    } catch (err) {
      console.error('Failed to load clubs:', err);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !body.trim()) {
      setError('Title and body are required');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        notification_type: notificationType,
        url: url.trim() || undefined,
        target_club_id: targetClubId || undefined
      };

      const res = await safeFetch(`${API_URL}/api/push/admin/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.data && res.data.success) {
        const { stats: deliveryStats } = res.data;
        setSuccess(`Notification sent to ${deliveryStats.sent}/${deliveryStats.total_targets} subscribers`);
        
        // Clear form
        setTitle('');
        setBody('');
        setUrl('');
        setTargetClubId('');
        
        // Reload data
        loadData();
      } else {
        setError(res.data?.detail || 'Failed to send notification');
      }
    } catch (err) {
      setError(err.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const NOTIFICATION_TYPES = [
    { value: 'general', label: 'General Announcement' },
    { value: 'dj_request', label: 'DJ Request Alert' },
    { value: 'bingo', label: 'Bingo Game Alert' },
    { value: 'event_reminder', label: 'Event Reminder' }
  ];

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-notifications-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Active Subscribers</p>
                <p className="text-2xl font-bold text-green-400">
                  {stats?.subscriptions?.active || 0}
                </p>
              </div>
              <Users className="h-8 w-8 text-green-400/20" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Sent</p>
                <p className="text-2xl font-bold text-blue-400">
                  {stats?.recent_delivery?.sent || 0}
                </p>
              </div>
              <Send className="h-8 w-8 text-blue-400/20" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Success Rate</p>
                <p className="text-2xl font-bold text-violet-400">
                  {stats?.recent_delivery?.success_rate || 0}%
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-violet-400/20" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Expired</p>
                <p className="text-2xl font-bold text-orange-400">
                  {stats?.subscriptions?.expired || 0}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-orange-400/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Notification Form */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
              <Bell className="h-5 w-5 text-violet-400" />
              Send Push Notification
            </CardTitle>
            <CardDescription className="text-slate-400">
              Send notifications to all subscribers or target a specific club
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendNotification} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="bg-red-900/20 border-red-900/50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="bg-green-900/20 border-green-900/50 text-green-400">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label className="text-sm text-slate-300">Notification Type</label>
                <Select value={notificationType} onValueChange={setNotificationType}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {NOTIFICATION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-slate-200">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-300">Target Club (Optional)</label>
                <Select value={targetClubId || "all"} onValueChange={(val) => setTargetClubId(val === "all" ? "" : val)}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-slate-200">
                    <SelectValue placeholder="All subscribers" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                    <SelectItem value="all" className="text-slate-200">All subscribers</SelectItem>
                    {clubs.map(club => (
                      <SelectItem key={club.club_id} value={club.club_id} className="text-slate-200">
                        {club.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-300">Title *</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Notification title"
                  maxLength={100}
                  className="bg-slate-900 border-slate-600 text-slate-200"
                  data-testid="notification-title-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-300">Message *</label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Notification message"
                  maxLength={500}
                  rows={3}
                  className="bg-slate-900 border-slate-600 text-slate-200 resize-none"
                  data-testid="notification-body-input"
                />
                <p className="text-xs text-slate-500">{body.length}/500 characters</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-300">Link URL (Optional)</label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="/events or https://..."
                  className="bg-slate-900 border-slate-600 text-slate-200"
                />
              </div>

              <Button
                type="submit"
                disabled={sending || !title.trim() || !body.trim()}
                className="w-full bg-violet-600 hover:bg-violet-700"
                data-testid="send-notification-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Notification
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recent History */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                <History className="h-5 w-5 text-violet-400" />
                Recent Notifications
              </CardTitle>
              <CardDescription className="text-slate-400">
                Last 10 notifications sent
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadData}
              disabled={loading}
              className="text-slate-400 hover:text-slate-200"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No notifications sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {history.map((notification, idx) => (
                  <div 
                    key={notification.notification_id || idx}
                    className="p-3 rounded-lg bg-slate-900/50 border border-slate-700/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-200 truncate">
                          {notification.title}
                        </p>
                        <p className="text-sm text-slate-400 line-clamp-2">
                          {notification.body}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300">
                          {notification.notification_type}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                      <span>
                        {notification.stats?.sent || 0}/{notification.stats?.total_targets || 0} delivered
                      </span>
                      <span>
                        {notification.created_at ? 
                          new Date(notification.created_at).toLocaleString('en-GB', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                          }) : 'Unknown'
                        }
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminNotificationsTab;
